import React, { useEffect, useState } from 'react';

import {
  Box,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Divider,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Icon,
  IconButton,
} from '@chakra-ui/react';
import { FaGripVertical, FaPlus, FaTrash } from 'react-icons/fa';

import { useColors } from './colors';
import { getEntityId, getEntityName } from '../utils/entityOptions.util';
import {
  cloneItemSpecsConfig,
  commaStringToMatchNames,
  commaStringToValues,
  emptyDefaultSpec,
  matchNamesToCommaString,
  resolveItemSpecsConfig,
  valuesToCommaString,
} from '../utils/itemSpecsConfig.util';

const reorderList = (list, fromIndex, toIndex) => {
  const next = [...list];
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
};

const DraggableSpecList = ({
  items,
  onChange,
  emptyLabel,
  showMatchNames = false,
}) => {
  const { textColor, borderColor, secondaryTextColor, inputColor, mutedBg } = useColors();
  const [dragIndex, setDragIndex] = useState(null);
  const [newName, setNewName] = useState('');
  const [newMatchNames, setNewMatchNames] = useState('');

  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    onChange(reorderList(items, dragIndex, index));
    setDragIndex(index);
  };
  const handleDragEnd = () => setDragIndex(null);

  const updateItem = (index, patch) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    const name = newName.trim();
    if (!name) return;
    onChange([
      ...items,
      {
        name,
        matchNames: commaStringToMatchNames(newMatchNames),
      },
    ]);
    setNewName('');
    setNewMatchNames('');
  };

  return (
    <VStack align="stretch" spacing={3}>
      {!items.length && (
        <Text fontSize="sm" color={secondaryTextColor}>
          {emptyLabel}
        </Text>
      )}

      {items.map((item, index) => (
        <HStack
          key={`${item.name}-${index}`}
          p={3}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={mutedBg}
          align="flex-start"
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          opacity={dragIndex === index ? 0.6 : 1}
          cursor="grab"
          _active={{ cursor: 'grabbing' }}
        >
          <Icon as={FaGripVertical} color="gray.400" mt={2} flexShrink={0} />
          <VStack align="stretch" flex={1} spacing={2}>
            <Input
              size="sm"
              value={item.name}
              onChange={(e) => updateItem(index, { name: e.target.value })}
              placeholder="Field name"
              color={inputColor}
              fontWeight="medium"
            />
            {showMatchNames && (
              <Input
                size="sm"
                value={matchNamesToCommaString(item.matchNames)}
                onChange={(e) =>
                  updateItem(index, { matchNames: commaStringToMatchNames(e.target.value) })
                }
                placeholder="Also matches (comma-separated, optional)"
                color={inputColor}
              />
            )}
          </VStack>
          <IconButton
            aria-label="Remove field"
            icon={<FaTrash />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => removeItem(index)}
          />
        </HStack>
      ))}

      <Box p={3} borderWidth="1px" borderStyle="dashed" borderColor={borderColor} borderRadius="md">
        <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={2}>
          Add field
        </Text>
        <VStack align="stretch" spacing={2}>
          <Input
            size="sm"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Field name"
            color={inputColor}
          />
          {showMatchNames && (
            <Input
              size="sm"
              value={newMatchNames}
              onChange={(e) => setNewMatchNames(e.target.value)}
              placeholder="Also matches (comma-separated, optional)"
              color={inputColor}
            />
          )}
          <Button
            size="sm"
            leftIcon={<FaPlus />}
            colorScheme="teal"
            variant="outline"
            alignSelf="flex-start"
            onClick={addItem}
            isDisabled={!newName.trim()}
          >
            Add
          </Button>
        </VStack>
      </Box>
    </VStack>
  );
};

const ExcludedSpecList = ({ items, onChange, emptyLabel }) => {
  const { textColor, borderColor, secondaryTextColor, inputColor, mutedBg } = useColors();
  const [newName, setNewName] = useState('');

  const updateItem = (index, patch) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = () => {
    const name = newName.trim();
    if (!name) return;
    onChange([...items, { name, matchNames: [] }]);
    setNewName('');
  };

  return (
    <VStack align="stretch" spacing={3}>
      {!items.length && (
        <Text fontSize="sm" color={secondaryTextColor}>
          {emptyLabel}
        </Text>
      )}
      {items.map((item, index) => (
        <HStack
          key={`${item.name}-${index}`}
          p={3}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          bg={mutedBg}
        >
          <Input
            size="sm"
            flex={1}
            value={item.name}
            onChange={(e) => updateItem(index, { name: e.target.value })}
            placeholder="Field name to exclude"
            color={inputColor}
          />
          <IconButton
            aria-label="Remove excluded field"
            icon={<FaTrash />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => removeItem(index)}
          />
        </HStack>
      ))}
      <HStack>
        <Input
          size="sm"
          flex={1}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Field name"
          color={inputColor}
        />
        <Button
          size="sm"
          leftIcon={<FaPlus />}
          colorScheme="teal"
          variant="outline"
          onClick={addItem}
          isDisabled={!newName.trim()}
        >
          Add
        </Button>
      </HStack>
    </VStack>
  );
};

const DefaultSpecRow = ({
  spec,
  index,
  onChange,
  onRemove,
  storeOptions,
  supplierOptions,
}) => {
  const { textColor, borderColor, secondaryTextColor, inputColor, mutedBg, backgroundCardColor } =
    useColors();

  const valuesStr = valuesToCommaString(spec.values);

  const handleValuesChange = (value) => {
    onChange(index, { values: commaStringToValues(value) });
  };

  const setPerSupplier = (checked) => {
    const supplierValues = { ...(spec.supplierValues ?? {}) };
    if (checked) {
      supplierOptions.forEach((s) => {
        const id = getEntityId(s);
        if (id && supplierValues[id] == null) {
          supplierValues[id] = valuesStr;
        }
      });
    }
    onChange(index, {
      perSupplier: checked,
      supplierValues: checked ? supplierValues : {},
    });
  };

  const setPerStore = (checked) => {
    const storeValues = { ...(spec.storeValues ?? {}) };
    if (checked) {
      storeOptions.forEach((s) => {
        const id = getEntityId(s);
        if (id && storeValues[id] == null) {
          storeValues[id] = valuesStr;
        }
      });
    }
    onChange(index, {
      perStore: checked,
      storeValues: checked ? storeValues : {},
    });
  };

  const updateSupplierValue = (supplierId, value) => {
    onChange(index, {
      supplierValues: { ...(spec.supplierValues ?? {}), [supplierId]: value },
    });
  };

  const updateStoreValue = (storeId, value) => {
    onChange(index, {
      storeValues: { ...(spec.storeValues ?? {}), [storeId]: value },
    });
  };

  return (
    <Box p={4} borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={mutedBg}>
      <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
        <FormControl flex={1} minW="200px">
          <FormLabel fontSize="xs" color={secondaryTextColor}>
            Field name
          </FormLabel>
          <Input
            size="sm"
            value={spec.name}
            onChange={(e) => onChange(index, { name: e.target.value })}
            color={inputColor}
            fontWeight="semibold"
          />
        </FormControl>
        <HStack spacing={2}>
          <Badge colorScheme="teal" variant="outline">
            Default
          </Badge>
          <IconButton
            aria-label="Remove default spec"
            icon={<FaTrash />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => onRemove(index)}
          />
        </HStack>
      </HStack>

      <FormControl mb={3}>
        <FormLabel fontSize="xs" color={secondaryTextColor}>
          Also matches (comma-separated, optional)
        </FormLabel>
        <Input
          size="sm"
          value={matchNamesToCommaString(spec.matchNames)}
          onChange={(e) => onChange(index, { matchNames: commaStringToMatchNames(e.target.value) })}
          color={inputColor}
        />
      </FormControl>

      {!spec.perSupplier && !spec.perStore && (
        <FormControl mb={3}>
          <FormLabel fontSize="xs" color={secondaryTextColor}>
            Values (comma-separated)
          </FormLabel>
          <Input
            size="sm"
            value={valuesStr}
            onChange={(e) => handleValuesChange(e.target.value)}
            placeholder="Rev & Bolt"
            color={inputColor}
          />
        </FormControl>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={spec.perSupplier || spec.perStore ? 3 : 0}>
        <FormControl display="flex" alignItems="center">
          <Switch
            id={`per-supplier-${index}`}
            isChecked={Boolean(spec.perSupplier)}
            onChange={(e) => setPerSupplier(e.target.checked)}
            colorScheme="teal"
            mr={2}
          />
          <FormLabel htmlFor={`per-supplier-${index}`} mb={0} fontSize="sm" color={textColor}>
            Different value per supplier
          </FormLabel>
        </FormControl>
        <FormControl display="flex" alignItems="center">
          <Switch
            id={`per-store-${index}`}
            isChecked={Boolean(spec.perStore)}
            onChange={(e) => setPerStore(e.target.checked)}
            colorScheme="teal"
            mr={2}
          />
          <FormLabel htmlFor={`per-store-${index}`} mb={0} fontSize="sm" color={textColor}>
            Different value per store
          </FormLabel>
        </FormControl>
      </SimpleGrid>

      {spec.perSupplier && (
        <Box mb={3}>
          <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={2}>
            Per supplier
          </Text>
          {supplierOptions.length === 0 ? (
            <Text fontSize="xs" color={secondaryTextColor}>
              No suppliers loaded. Refresh the page or check supplier list API.
            </Text>
          ) : (
            <TableContainer borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={backgroundCardColor}>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Supplier</Th>
                    <Th>Value(s)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {supplierOptions.map((s) => {
                    const id = getEntityId(s);
                    return (
                      <Tr key={id}>
                        <Td fontSize="xs">{getEntityName(s)}</Td>
                        <Td>
                          <Input
                            size="xs"
                            value={spec.supplierValues?.[id] ?? valuesStr}
                            onChange={(e) => updateSupplierValue(id, e.target.value)}
                            color={inputColor}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {spec.perStore && (
        <Box>
          <Text fontSize="xs" fontWeight="semibold" color={textColor} mb={2}>
            Per store
          </Text>
          {storeOptions.length === 0 ? (
            <Text fontSize="xs" color={secondaryTextColor}>
              No stores loaded.
            </Text>
          ) : (
            <TableContainer borderWidth="1px" borderColor={borderColor} borderRadius="md" bg={backgroundCardColor}>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Store</Th>
                    <Th>Value(s)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {storeOptions.map((s) => {
                    const id = getEntityId(s);
                    return (
                      <Tr key={id}>
                        <Td fontSize="xs">{getEntityName(s)}</Td>
                        <Td>
                          <Input
                            size="xs"
                            value={spec.storeValues?.[id] ?? valuesStr}
                            onChange={(e) => updateStoreValue(id, e.target.value)}
                            color={inputColor}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

const ItemSpecsSettingsSection = ({
  itemSpecsConfig,
  storeOptions = [],
  supplierOptions = [],
  onSave,
  isSaving = false,
  embedded = false,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor } = useColors();
  const [draft, setDraft] = useState(() => resolveItemSpecsConfig(itemSpecsConfig));
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    setDraft(resolveItemSpecsConfig(itemSpecsConfig));
    setIsDirty(false);
  }, [itemSpecsConfig]);

  const patchDraft = (updater) => {
    setDraft((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return next;
    });
    setIsDirty(true);
  };

  const updateDefault = (index, patch) => {
    patchDraft((prev) => ({
      ...prev,
      defaults: prev.defaults.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    }));
  };

  const removeDefault = (index) => {
    patchDraft((prev) => ({
      ...prev,
      defaults: prev.defaults.filter((_, i) => i !== index),
    }));
  };

  const addDefault = () => {
    patchDraft((prev) => ({
      ...prev,
      defaults: [...prev.defaults, emptyDefaultSpec()],
    }));
  };

  const handleSave = async () => {
    const cleaned = cloneItemSpecsConfig(draft);
    cleaned.defaults = cleaned.defaults
      .map((d) => ({
        ...d,
        name: d.name.trim(),
        values: d.values ?? [],
      }))
      .filter((d) => d.name);
    cleaned.orderedAfterDefaults = cleaned.orderedAfterDefaults
      .map((r) => ({ ...r, name: r.name.trim() }))
      .filter((r) => r.name);
    cleaned.endingOrdered = cleaned.endingOrdered
      .map((r) => ({ ...r, name: r.name.trim() }))
      .filter((r) => r.name);
    cleaned.excluded = cleaned.excluded
      .map((r) => ({ ...r, name: r.name.trim() }))
      .filter((r) => r.name);

    const saved = await onSave?.(cleaned);
    if (saved !== false) {
      setIsDirty(false);
    }
  };

  const body = (
    <>
      {!embedded && (
        <>
          <Text color={textColor} fontSize="md" fontWeight="semibold" mb={1}>
            Item specifics settings
          </Text>
          <Text color={secondaryTextColor} fontSize="sm" mb={4}>
            Configure how eBay item specifics are built during optimization. Add or remove fields,
            reorder lists, and set per-supplier or per-store default values.
          </Text>
        </>
      )}
      {embedded && (
        <Text color={secondaryTextColor} fontSize="sm" mb={4}>
          Configure how eBay item specifics are built during optimization. Add or remove fields,
          reorder lists, and set per-supplier or per-store default values.
        </Text>
      )}

      <Accordion allowMultiple defaultIndex={[0, 1]}>
        <AccordionItem border="none">
          <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
            <Box flex="1" textAlign="left">
              <Text fontWeight="semibold" color={textColor} fontSize="sm">
                Default specifics
              </Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                Always applied when missing on the listing
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={0} pb={4}>
            <VStack align="stretch" spacing={3}>
              {draft.defaults.map((spec, index) => (
                <DefaultSpecRow
                  key={`default-${index}-${spec.name}`}
                  spec={spec}
                  index={index}
                  onChange={updateDefault}
                  onRemove={removeDefault}
                  storeOptions={storeOptions}
                  supplierOptions={supplierOptions}
                />
              ))}
              <Button
                size="sm"
                leftIcon={<FaPlus />}
                variant="outline"
                colorScheme="teal"
                alignSelf="flex-start"
                onClick={addDefault}
              >
                Add default spec
              </Button>
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        <Divider />

        <AccordionItem border="none">
          <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
            <Box flex="1" textAlign="left">
              <Text fontWeight="semibold" color={textColor} fontSize="sm">
                Ordered fields (after defaults)
              </Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                Drag to reorder · optional match names
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={0} pb={4}>
            <DraggableSpecList
              items={draft.orderedAfterDefaults}
              onChange={(orderedAfterDefaults) => patchDraft((p) => ({ ...p, orderedAfterDefaults }))}
              emptyLabel="No ordered fields yet"
              showMatchNames
            />
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none">
          <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
            <Box flex="1" textAlign="left">
              <Text fontWeight="semibold" color={textColor} fontSize="sm">
                Ending ordered fields
              </Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                Drag to reorder · placed at the end of the specifics list
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={0} pb={4}>
            <DraggableSpecList
              items={draft.endingOrdered}
              onChange={(endingOrdered) => patchDraft((p) => ({ ...p, endingOrdered }))}
              emptyLabel="No ending fields yet"
              showMatchNames
            />
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none">
          <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
            <Box flex="1" textAlign="left">
              <Text fontWeight="semibold" color={textColor} fontSize="sm">
                Excluded specifics
              </Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                Removed from the listing during optimization
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel px={0}>
            <ExcludedSpecList
              items={draft.excluded}
              onChange={(excluded) => patchDraft((p) => ({ ...p, excluded }))}
              emptyLabel="No excluded fields"
            />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <HStack justify="flex-end" mt={6} pt={4} borderTopWidth="1px" borderColor={borderColor}>
        {isDirty && (
          <Text fontSize="xs" color={secondaryTextColor} mr="auto">
            Unsaved changes
          </Text>
        )}
        <Button
          colorScheme="teal"
          onClick={handleSave}
          isLoading={isSaving}
          loadingText="Saving…"
        >
          Update item specs settings
        </Button>
      </HStack>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Box
      p={5}
      bg={backgroundCardColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
    >
      {body}
    </Box>
  );
};

export default ItemSpecsSettingsSection;
